import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { Shield, Eye, Database, Users, Lock, Globe, Mail, Phone, MapPin } from 'lucide-react';
import SEOHead from './SEOHead';
import { getBreadcrumbStructuredData } from '../utils/structuredData';
import Header from './Header';
import Footer from './Footer';

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const privacyData = t.legal.privacy;

  const breadcrumbData = getBreadcrumbStructuredData([
    { name: 'Home', url: 'https://laimu.mx/' },
    { name: 'Privacy Policy', url: 'https://laimu.mx/privacy' }
  ]);

  const seoTitle = language === 'en' 
    ? 'Privacy Policy - LAIMU Trantor | Data Protection & Security'
    : 'Política de Privacidad - LAIMU Trantor | Protección y Seguridad de Datos';
    
  const seoDescription = language === 'en'
    ? 'Learn how LAIMU Trantor protects your personal data and privacy. Comprehensive privacy policy covering data collection, usage, security measures, and your rights under GDPR and local regulations.'
    : 'Conoce cómo LAIMU Trantor protege tus datos personales y privacidad. Política de privacidad integral que cubre recolección de datos, uso, medidas de seguridad y tus derechos bajo GDPR y regulaciones locales.';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title={seoTitle}
        description={seoDescription}
        url="/privacy"
        structuredData={breadcrumbData}
        keywords={language === 'en' 
          ? 'privacy policy, data protection, GDPR compliance, personal data security, LAIMU privacy, data collection policy, user rights, data processing'
          : 'política privacidad, protección datos, cumplimiento GDPR, seguridad datos personales, privacidad LAIMU, política recolección datos, derechos usuario, procesamiento datos'
        }
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <div className="flex items-center mb-8 border-b border-gray-100 pb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#e0f2fe' }}>
                  <Shield className="w-6 h-6" style={{ color: '#115d7b' }} />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{privacyData.title}</h1>
                  <p className="text-gray-500 text-sm mt-1">{privacyData.lastUpdated}: {privacyData.date}</p>
                </div>
              </div>

              <div className="prose max-w-none text-gray-700 space-y-8">
                
                {/* 1. Introduction */}
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5" style={{ color: '#115d7b' }} />
                    {privacyData.introduction.title}
                  </h2>
                  <p className="leading-relaxed">{privacyData.introduction.content}</p>
                </section>

                {/* 2. Scope */}
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.scope.title}
                  </h2>
                  <p className="leading-relaxed mb-3">{privacyData.scope.content}</p>
                  <p className="text-sm bg-gray-50 border-l-4 border-yellow-500 p-3 italic text-gray-600">
                    {privacyData.scope.exclusions}
                  </p>
                </section>

                {/* 3. Controller */}
                <section>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.controller.title}
                  </h2>
                  <p className="leading-relaxed">{privacyData.controller.content}</p>
                </section>

                {/* 4. Data Collection */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Database className="w-5 h-5" style={{ color: '#115d7b' }} />
                    {privacyData.dataCollection.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.dataCollection.intro}</p>
                  
                  <div className="space-y-4 mb-6">
                    {Object.values(privacyData.dataCollection.categories).map((cat: any, index: number) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                        <h4 className="font-bold text-gray-900 text-sm mb-1">{cat.title}</h4>
                        <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                          {cat.items.map((item: string, i: number) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <h3 className="font-bold text-gray-900 text-base mb-3">
                    {privacyData.dataCollection.sources.title}
                  </h3>
                  <div className="space-y-4">
                    <div className="pl-4 border-l-2 border-[#115d7b]">
                      <h4 className="font-bold text-sm text-gray-800">{privacyData.dataCollection.sources.direct.title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{privacyData.dataCollection.sources.direct.content}</p>
                      <ul className="list-disc list-inside text-xs text-gray-500 space-y-0.5 mt-1">
                        {privacyData.dataCollection.sources.direct.examples.map((ex: string, i: number) => (
                          <li key={i}>{ex}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="pl-4 border-l-2 border-[#115d7b]">
                      <h4 className="font-bold text-sm text-gray-800">{privacyData.dataCollection.sources.automated.title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{privacyData.dataCollection.sources.automated.content}</p>
                    </div>
                    <div className="pl-4 border-l-2 border-[#115d7b]">
                      <h4 className="font-bold text-sm text-gray-800">{privacyData.dataCollection.sources.thirdParties.title}</h4>
                      <p className="text-xs text-gray-600 mt-1">{privacyData.dataCollection.sources.thirdParties.content}</p>
                      <ul className="list-disc list-inside text-xs text-gray-500 space-y-0.5 mt-1">
                        {privacyData.dataCollection.sources.thirdParties.sources.map((src: string, i: number) => (
                          <li key={i}>{src}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>

                {/* 5. Legal Basis */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.legalBasis.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.legalBasis.intro}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.values(privacyData.legalBasis.bases).map((base: any, index: number) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg">
                        <h4 className="font-bold text-sm text-[#115d7b] mb-1">{base.title}</h4>
                        <p className="text-xs text-gray-600">{base.content}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 6. Data Use */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5" style={{ color: '#115d7b' }} />
                    {privacyData.dataUse.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.dataUse.intro}</p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border border-gray-250 rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gray-100 text-gray-800 text-left font-bold border-b border-gray-250">
                          <th className="p-3">{language === 'en' ? 'Purpose' : 'Propósito'}</th>
                          <th className="p-3">{language === 'en' ? 'Data Types' : 'Tipo de Datos'}</th>
                          <th className="p-3">{language === 'en' ? 'Legal Basis' : 'Base Legal'}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {Object.values(privacyData.dataUse.purposes).map((purpose: any, index: number) => (
                          <tr key={index} className="hover:bg-gray-50/50">
                            <td className="p-3 font-semibold text-gray-900">{purpose.purpose}</td>
                            <td className="p-3 text-gray-600">{purpose.dataTypes}</td>
                            <td className="p-3 text-gray-500 italic">{purpose.legalBasis}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 bg-blue-50/40 p-4 border border-blue-100 rounded-lg">
                    <h4 className="font-bold text-sm text-gray-900 mb-1">{privacyData.dataUse.marketing.title}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{privacyData.dataUse.marketing.content}</p>
                  </div>
                </section>

                {/* 7. Sharing */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.sharing.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.sharing.intro}</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                      <h4 className="font-bold text-sm text-gray-900 mb-1">{privacyData.sharing.categories.internal.title}</h4>
                      <p className="text-xs text-gray-600">{privacyData.sharing.categories.internal.content}</p>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                      <h4 className="font-bold text-sm text-gray-900 mb-2">{privacyData.sharing.categories.external.title}</h4>
                      <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                        {privacyData.sharing.categories.external.items.map((item: string, i: number) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                      <h4 className="font-bold text-sm text-gray-900 mb-2">{privacyData.sharing.categories.specific.title}</h4>
                      <ul className="list-disc list-inside text-xs text-gray-600 space-y-1">
                        {privacyData.sharing.categories.specific.items.map((item: string, i: number) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <h3 className="font-bold text-gray-900 text-sm mt-5 mb-2">
                    {privacyData.sharing.circumstances.title}
                  </h3>
                  <ul className="list-disc list-inside text-xs text-gray-600 space-y-1 pl-2">
                    {privacyData.sharing.circumstances.items.map((item: string, i: number) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </section>

                {/* 8. International Transfers */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.internationalTransfers.title}
                  </h2>
                  <p className="leading-relaxed mb-3">{privacyData.internationalTransfers.content}</p>
                  <ul className="list-disc list-inside text-xs text-gray-600 space-y-1 pl-2">
                    {privacyData.internationalTransfers.safeguards.map((item: string, i: number) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </section>

                {/* 9. Data Security */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Lock className="w-5 h-5" style={{ color: '#115d7b' }} />
                    {privacyData.dataSecurity.title}
                  </h2>
                  <p className="leading-relaxed mb-3">{privacyData.dataSecurity.content}</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600 pl-2 mb-3 list-disc list-inside">
                    {privacyData.dataSecurity.measures.map((item: string, i: number) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-500 italic mt-2">{privacyData.dataSecurity.breach}</p>
                </section>

                {/* 10. Data Retention */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.retention.title}
                  </h2>
                  <p className="leading-relaxed mb-3">{privacyData.retention.intro}</p>
                  <p className="leading-relaxed mb-4 text-xs text-gray-500 italic">{privacyData.retention.criteria}</p>
                  <div className="space-y-3">
                    {Object.values(privacyData.retention.periods).map((p: any, i: number) => (
                      <div key={i} className="flex justify-between text-xs border-b border-gray-100 pb-2">
                        <span className="font-bold text-gray-900">{p.type}</span>
                        <span className="text-gray-600">{p.period}</span>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 11. User Rights */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5" style={{ color: '#115d7b' }} />
                    {privacyData.rights.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.rights.intro}</p>
                  <div className="space-y-4 pl-2">
                    {Object.values(privacyData.rights.list).map((right: any, index: number) => (
                      <div key={index}>
                        <h4 className="font-bold text-sm text-[#115d7b] mb-0.5">{right.title}</h4>
                        <p className="text-xs text-gray-600 leading-relaxed">{right.description}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 12. Contact */}
                <section className="border-t border-gray-100 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">
                    {privacyData.contact.title}
                  </h2>
                  <p className="leading-relaxed mb-4">{privacyData.contact.content}</p>
                  <div className="bg-gray-50 border border-gray-200 p-5 rounded-xl flex flex-col md:flex-row gap-6 justify-between items-start md:items-center">
                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-center gap-2.5">
                        <Mail className="w-4 h-4 text-[#115d7b] flex-shrink-0" />
                        <strong>Email:</strong> privacy@laimu.mx
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Phone className="w-4 h-4 text-[#115d7b] flex-shrink-0" />
                        <strong>{t.contact.phone}:</strong> +52 81 1107 1450
                      </div>
                      <div className="flex items-start gap-2.5">
                        <MapPin className="w-4 h-4 text-[#115d7b] mt-0.5 flex-shrink-0" />
                        <div>
                          <strong>{t.contact.address.line1}</strong><br />
                          <span className="text-xs text-gray-500">
                            {t.contact.address.line2}<br />
                            {t.contact.address.city}, {t.contact.address.state} {t.contact.address.zip}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 leading-relaxed">{privacyData.contact.response}</p>
                </section>
                
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;